import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateUserRequest, UpdateUserRequest, User } from '../types';
import { apiClient } from '../services/api';
import { updateArrayQueries } from '../utils/queryCache';

type SaveUserInput = {
  id?: string;
  data: CreateUserRequest | UpdateUserRequest;
  isSuperAdmin: boolean;
};

export const useUserMutations = () => {
  const queryClient = useQueryClient();

  const invalidateUsers = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['users'] }),
      queryClient.invalidateQueries({ queryKey: ['admin'] }),
    ]);
  };

  const saveUserMutation = useMutation({
    mutationFn: async ({ id, data, isSuperAdmin }: SaveUserInput) => {
      if (id) return apiClient.updateUser(id, data as UpdateUserRequest);

      const createData = data as CreateUserRequest;
      if (isSuperAdmin) {
        return apiClient.createCompanyUser(createData.companyId!, createData);
      }
      return apiClient.createEmployee(createData);
    },
    onSuccess: invalidateUsers,
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteUser(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['users'] });
      const snapshots = queryClient.getQueriesData<User[]>({ queryKey: ['users'] });
      updateArrayQueries<User>(queryClient, 'users', users => users.filter(user => user.id !== id));
      return { snapshots };
    },
    onError: (_error, _id, context) => {
      context?.snapshots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: invalidateUsers,
  });

  return {
    saveUserMutation,
    deleteUserMutation,
  };
};
